import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Form } from '@kuzmycz/react-form-ula';
import { Button, Modal, Switch } from '../../../../Components/uielements';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import DropDown from '../../../../Components/uielements/dropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  ModalGap,
  OutlineButton,
  SwitcherLabel,
} from '../../../../services/settings/appStyle/global.style';

import {
  ModalWrapper,
  ModalButtonWrapper,
  Columns,
  PermissionRowWrapper,
  PermissionLabel,
  PermissionActions,
  PermissionAction,
  RangeFieldWrapper,
  PermissionNumberField,
  PermissionNumberLabel,
  PermissionNumberInput,
  InfoIconWrapper,
  Tooltip,
  TooltipTitle,
  TooltipText,
} from '../Subscription.style';

import SubscriptionPlanService, {
  getDefaultPermissions,
} from '../../../../services/bioplus-services/subscription-plan';

import { IntlMessages } from '../../../../helper/langHelper';
import { ModalTitle } from '../../../../Components/uielements/modal/modal.style';
import AddPlanDetails from './addPlanDetails';
import { templateList } from '../../../../services/bioplus-services/emailTemplate';

import {
  getInitialPermissionValues,
  isLimitApplicable,
  isPermissionToggleable,
  isRangeApplicable,
  normalizePlanV2,
} from '../helper';

import { InfoOutlined } from '@mui/icons-material';

const getSubscriptionPlanUrlBasedOnEditOrCreate = async (actionEditOrCreatePlan, values) => {
  if (actionEditOrCreatePlan === 'editPlan') {
    return await SubscriptionPlanService.updatePlan(values);
  }

  if (actionEditOrCreatePlan === 'createPlan') {
    return await SubscriptionPlanService.createPlan(values);
  }
};

/**
 * Custom controlled number field.
 * Used only for permission Range and Limit.
 */
const PermissionNumberInputField = ({ label, value, onChange }) => {
  const handleChange = (e) => {
    const nextValue = e.target.value;

    // Allow user to clear the input
    if (nextValue === '') {
      onChange({
        target: {
          value: '',
        },
      });
      return;
    }

    // Do not allow negative values
    if (Number(nextValue) < 0) {
      onChange({
        target: {
          value: '0',
        },
      });
      return;
    }

    onChange({
      target: {
        value: nextValue,
      },
    });
  };

  const handleKeyDown = (e) => {
    // Prevent negative numbers, exponent and plus
    if (['-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <PermissionNumberField>
      <PermissionNumberLabel>{label}</PermissionNumberLabel>

      <PermissionNumberInput
        type="number"
        min="0"
        step="1"
        value={value ?? ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onWheel={(e) => e.currentTarget.blur()}
      />
    </PermissionNumberField>
  );
};

/**
 * One row per configurable permission.
 */
const PermissionRow = ({ permission, values, onChange }) => {
  const toggleable = isPermissionToggleable(permission);
  const rangeApplicable = isRangeApplicable(permission.label);
  const limitApplicable = isLimitApplicable(permission.label);

  if (!toggleable) return null;

  const handleToggle = (field) => {
    onChange(permission.label, {
      ...values,
      [field]: !values[field],
    });
  };

  const handleRangeChange = (e) => {
    onChange(permission.label, {
      ...values,
      range: e.target.value,
    });
  };

  const handleLimitChange = (e) => {
    onChange(permission.label, {
      ...values,
      limit: e.target.value,
    });
  };

  return (
    <>
      {' '}
      {permission.description && (
        <TooltipText style={{ display: 'flex', gap: '5px' }}>
          <InfoOutlinedIcon /> {permission.description}
        </TooltipText>
      )}
      <PermissionRowWrapper>
        <PermissionLabel>{permission.label}</PermissionLabel>

        <PermissionActions>
          {['isView', 'isCreate', 'isManage', 'isReport'].map((field) => (
            <PermissionAction key={field}>
              <Switch handleToggle={() => handleToggle(field)} checked={!!values[field]} />

              <SwitcherLabel>
                {field === 'isView' && <IntlMessages id="text.feature.view" defaultValue="View" />}

                {field === 'isCreate' &&
                  (permission.label === 'user' ? (
                    <IntlMessages id="text.feature.invite" defaultValue="Invite" />
                  ) : (
                    <IntlMessages id="text.feature.create" defaultValue="Create" />
                  ))}

                {field === 'isManage' && (
                  <IntlMessages id="text.feature.manager" defaultValue="Manage" />
                )}

                {field === 'isReport' && (
                  <IntlMessages id="text.feature.report" defaultValue="Report" />
                )}
              </SwitcherLabel>
            </PermissionAction>
          ))}
        </PermissionActions>

        {rangeApplicable && (
          <RangeFieldWrapper>
            <PermissionNumberInputField
              label={
                permission.label?.toLowerCase() === 'geofence'
                  ? 'Range (Hectare)'
                  : 'Range (Dia in Km)'
              }
              value={values.range}
              onChange={handleRangeChange}
            />
          </RangeFieldWrapper>
        )}

        {limitApplicable && (
          <RangeFieldWrapper>
            <PermissionNumberInputField
              label="Limit"
              value={values.limit ?? 0}
              onChange={handleLimitChange}
            />
          </RangeFieldWrapper>
        )}
      </PermissionRowWrapper>
    </>
  );
};

const SubscriptionModalV2 = ({ onClose, data, actionEditOrCreatePlan }) => {
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState();

  const [isIncremental, setIsIncremental] = useState(
    data?.isIncremental === 1 || data?.isIncremental === 'Yes'
  );

  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);

  const [planFields, setPlanFields] = useState([
    {
      id: 1,
      value: '',
    },
  ]);

  const [permissionValues, setPermissionValues] = useState({});

  const [showTooltip, setShowTooltip] = useState(false);

  const queryClient = useQueryClient();

  const emailTemplateList = useQuery(['emailTemplates'], () => templateList());

  const emailTemplateData = emailTemplateList?.data ?? [];

  const emailTemplateOptions = emailTemplateData.map((template) => ({
    label: template.title,
    value: template._id,
  }));

  const defaultPermissionsQuery = useQuery(['defaultPermissions'], () => getDefaultPermissions());

  const defaultPermissions = defaultPermissionsQuery?.data?.permissions?.defaultPermissions ?? [];

  /**
   * Populate permissions.
   *
   * When editing, existing values from data.features are used.
   * This also automatically populates Range and Limit.
   */
  useEffect(() => {
    if (defaultPermissions.length > 0) {
      setPermissionValues(
        getInitialPermissionValues(
          defaultPermissions,
          actionEditOrCreatePlan === 'editPlan' ? data?.features ?? [] : []
        )
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPermissionsQuery?.data, actionEditOrCreatePlan]);

  /**
   * Populate existing Add Plan Details fields when editing.
   */
  useEffect(() => {
    if (
      actionEditOrCreatePlan === 'editPlan' &&
      data?.featureList &&
      Array.isArray(data.featureList)
    ) {
      const existingFields = data.featureList.map((feature, index) => ({
        id: index + 1,
        value: feature,
      }));

      setPlanFields(existingFields.length > 0 ? existingFields : [{ id: 1, value: '' }]);
    }
  }, [actionEditOrCreatePlan, data]);

  const handlePermissionChange = (label, nextValues) => {
    setPermissionValues((prev) => ({
      ...prev,
      [label]: nextValues,
    }));
  };

  const mutation = useMutation(
    (values) => getSubscriptionPlanUrlBasedOnEditOrCreate(actionEditOrCreatePlan, values),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('subscriptionPlans');
        onClose();
      },

      onError: (error) => {
        alert(`Error: ${error?.response?.data?.message}`);
        onClose();
      },
    }
  );

  const handleSubmit = (values) => {
    const payload = normalizePlanV2({
      formValues: values,
      defaultPermissions,
      permissionValues,
      planFields,
      isIncremental,
      templateId: selectedEmailTemplate?.value,
    });

    if (selectedBillingPeriod) {
      payload.billingPeriod = selectedBillingPeriod.value;
    }

    if (actionEditOrCreatePlan === 'editPlan') {
      payload._id = data?._id;
    }

    mutation.mutate(payload);
  };

  const handleSelectedBillingPeriod = (value) => {
    setSelectedBillingPeriod(value);
  };

  const handleSelectedEmailTemplate = (value) => {
    setSelectedEmailTemplate(value);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      isUppercase={true}
      title={
        actionEditOrCreatePlan === 'editPlan' ? (
          <IntlMessages id="modal.title.edit.subscription" />
        ) : (
          <IntlMessages id="modal.title.subscription" />
        )
      }
    >
      <Form initialValues={data} onSubmit={handleSubmit}>
        <ModalWrapper>
          <ModalGap>
            <Columns>
              <BioPlusTextField name="description" label="Description" fullWidth />

              <BioPlusTextField name="labelName" label="Display Name" fullWidth />
            </Columns>

            <Columns>
              <DropDown
                selectedOption={handleSelectedBillingPeriod}
                options={[
                  {
                    label: 'Monthly',
                    value: 'month',
                  },
                  {
                    label: 'Annually',
                    value: 'year',
                  },
                  {
                    label: 'Daily',
                    value: 'day',
                  },
                ]}
                name="billingPeriod"
                defaultValue={data?.billingPeriod}
                label={<IntlMessages id="input.label.BillingPeriod" />}
              />

              <BioPlusTextField
                name="baseCost"
                label="Base Cost"
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }

                  if (e.key === '0' && e.target.value === '') {
                    e.preventDefault();
                  }
                }}
                onInput={(e) => {
                  const value = e.target.value;

                  if (value === '') return;

                  if (Number(value) < 1) {
                    e.target.value = '1';
                  }
                }}
              />
            </Columns>

            <DropDown
              selectedOption={handleSelectedEmailTemplate}
              options={emailTemplateOptions}
              name="templateId"
              label={data?.templateId ? 'Select an Email Template' : undefined}
              placeholder="Select an Email Template"
              defaultValue={
                data?.templateId
                  ? emailTemplateData.find((template) => template._id === data.templateId)?.title
                  : undefined
              }
            />

            <Columns>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Switch
                  handleToggle={() => setIsIncremental(!isIncremental)}
                  checked={isIncremental}
                />

                <SwitcherLabel>
                  <IntlMessages id="input.label.isIncremental" />
                </SwitcherLabel>

                <InfoIconWrapper
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={() => setShowTooltip(!showTooltip)}
                >
                  <InfoOutlined
                    style={{
                      width: '19px',
                      height: '19px',
                      color: '#E6B471',
                    }}
                  />

                  <Tooltip show={showTooltip}>
                    <TooltipTitle>Multiple Properties</TooltipTitle>

                    <TooltipText>
                      When enabled, this plan allows members to manage multiple properties under a
                      single subscription.
                    </TooltipText>
                  </Tooltip>
                </InfoIconWrapper>
              </div>

              <div />
            </Columns>

            <ModalTitle size="header3" isNotCapitalize={false} isUppercase={true}>
              Permissions
            </ModalTitle>

            {defaultPermissionsQuery.isLoading ? (
              <SwitcherLabel>Loading default permissions...</SwitcherLabel>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gap: '20px',
                  justifyContent: 'center',
                }}
              >
                {defaultPermissions.map((permission) => (
                  <PermissionRow
                    key={permission._id ?? permission.label}
                    permission={permission}
                    values={permissionValues[permission.label] || {}}
                    onChange={handlePermissionChange}
                  />
                ))}
              </div>
            )}

            <ModalTitle size="header3" isNotCapitalize={false} isUppercase={true}>
              Add Plan Details
            </ModalTitle>

            <AddPlanDetails planFields={planFields} setPlanFields={setPlanFields} />
          </ModalGap>

          <ModalButtonWrapper>
            <OutlineButton onClick={onClose}>
              <IntlMessages id="modal.button.cancel" />
            </OutlineButton>

            <Button
              varient="normal"
              type="submit"
              disabled={mutation?.isLoading || defaultPermissionsQuery.isLoading}
            >
              {actionEditOrCreatePlan === 'editPlan' ? (
                <IntlMessages id="button.text.update" />
              ) : (
                <IntlMessages id="button.text.create" />
              )}

              {mutation?.isLoading ? '...' : ''}
            </Button>
          </ModalButtonWrapper>
        </ModalWrapper>
      </Form>
    </Modal>
  );
};

export default SubscriptionModalV2;
